// src/ProductShellStateBanner/ProductShellStateBanner.tsx
import { Alert, Box, Button } from "@mui/material";

// src/utils/productCopy.ts
function prettifyKey(key) {
  const words = key.replace(/([a-z])([A-Z])/g, "$1 $2").split(/[_\s]+/).filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
var REASON_COPY = {
  not_entitled: "This product is not part of your current agreement",
  contract_expired: "Your contract for this product has expired",
  disabled_by_config: "This product is turned off for your rooftop",
  credit_limit_exceeded: "Your outstanding balance has reached its limit \u2014 new orders are paused",
  payment_required: "A payment is required before new orders",
  payment_failed: "Your latest payment did not go through",
  missing_payment_method: "No payment method is on file",
  administratively_suspended: "This product is temporarily suspended",
  history_window: "Your history stays viewable while your enrollment is inactive"
};
function reasonText(reason) {
  if (!reason) return null;
  return REASON_COPY[reason.key] ?? prettifyKey(reason.key);
}
function actionLabel(type) {
  const labels = {
    pay_now: "Pay now",
    update_payment_method: "Update payment method",
    contact_sales: "Contact sales",
    request_access: "Request access"
  };
  return labels[type] ?? prettifyKey(type);
}

// src/ProductShellStateBanner/ProductShellStateBanner.tsx
import { jsx } from "react/jsx-runtime";
var DEFAULT_TEXT = {
  limited: "Some actions are currently paused for this product",
  read_only: "This product is view-only"
};
var ProductShellStateBanner = ({
  variant,
  reason,
  text,
  actions = [],
  onStateAction
}) => {
  const message = text ?? reasonText(reason) ?? DEFAULT_TEXT[variant];
  const severity = variant === "limited" ? "warning" : "info";
  return /* @__PURE__ */ jsx(
    Alert,
    {
      severity,
      sx: {
        alignItems: "center",
        border: 1,
        borderColor: `${severity}.main`,
        "& .MuiAlert-action": { alignItems: "center", py: 0 }
      },
      action: actions.length > 0 ? /* @__PURE__ */ jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: actions.map((action) => /* @__PURE__ */ jsx(Button, { color: severity, size: "small", onClick: () => onStateAction?.(action), children: actionLabel(action.type) }, action.type)) }) : void 0,
      children: message
    }
  );
};
var ProductShellStateBanner_default = ProductShellStateBanner;

// src/ProductAdvertise/ProductAdvertise.tsx
import { useState } from "react";
import { Box as Box2, Button as Button2, Typography } from "@mui/material";
import { BiPlay } from "react-icons/bi";
import { HeroPanel, heroButtonSx, heroLedeSx, heroTitleSx } from "@hopdrive/sdk-ui";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var ProductAdvertise = ({ content, defaultPlaying = false }) => {
  const [playing, setPlaying] = useState(defaultPlaying);
  return /* @__PURE__ */ jsxs(Box2, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsxs(HeroPanel, { glow: (theme) => theme.palette.utility.main, children: [
      /* @__PURE__ */ jsxs(Box2, { children: [
        /* @__PURE__ */ jsx2(Typography, { component: "h2", sx: heroTitleSx, children: content.headline }),
        /* @__PURE__ */ jsx2(Typography, { sx: heroLedeSx, children: content.lede })
      ] }),
      content.ctas.length > 0 && /* @__PURE__ */ jsx2(Box2, { sx: { display: "flex", gap: 1.5, flexWrap: "wrap" }, children: content.ctas.map((cta, i) => /* @__PURE__ */ jsx2(
        Button2,
        {
          onClick: cta.onClick,
          startIcon: cta.icon,
          variant: i === 0 ? "contained" : "outlined",
          color: "primary",
          sx: heroButtonSx(i === 0 ? "primary" : "quiet"),
          children: cta.label
        },
        cta.label
      )) })
    ] }),
    content.media != null && /* @__PURE__ */ jsx2(Box2, { sx: { borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider" }, children: playing ? content.media : /* @__PURE__ */ jsx2(
      Box2,
      {
        role: "button",
        tabIndex: 0,
        "aria-label": "Play the product overview",
        onClick: () => setPlaying(true),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") setPlaying(true);
        },
        sx: {
          cursor: "pointer",
          minHeight: 280,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.900",
          color: "common.white"
        },
        children: /* @__PURE__ */ jsx2(
          Box2,
          {
            sx: {
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "common.white",
              color: "grey.900",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 36
            },
            children: /* @__PURE__ */ jsx2(BiPlay, {})
          }
        )
      }
    ) }),
    content.benefits.length > 0 && /* @__PURE__ */ jsx2(Box2, { sx: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2 }, children: content.benefits.map((benefit) => /* @__PURE__ */ jsxs(
      Box2,
      {
        sx: { p: 2.5, borderRadius: 2, border: 1, borderColor: "divider", bgcolor: "background.paper" },
        children: [
          /* @__PURE__ */ jsx2(
            Box2,
            {
              sx: {
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                color: "text.primary",
                fontSize: 22,
                mb: 1.5
              },
              children: benefit.icon
            }
          ),
          /* @__PURE__ */ jsx2(Typography, { sx: { fontWeight: 600, mb: 0.5 }, children: benefit.title }),
          /* @__PURE__ */ jsx2(Typography, { variant: "body2", sx: { color: "text.secondary" }, children: benefit.body })
        ]
      },
      benefit.title
    )) })
  ] });
};
var ProductAdvertise_default = ProductAdvertise;

// src/ProductShellAttentionStrip/ProductShellAttentionStrip.tsx
import { Box as Box3, Typography as Typography2 } from "@mui/material";
import { BiChevronDown, BiChevronRight, BiError, BiErrorCircle, BiInfoCircle } from "react-icons/bi";

// src/utils/attentionSeverity.ts
var SEVERITY_RANK = { error: 0, warning: 1, info: 2 };
function topSeverity(items) {
  if (items.length === 0) return null;
  return items.reduce(
    (top, item) => SEVERITY_RANK[item.severity] < SEVERITY_RANK[top] ? item.severity : top,
    "info"
  );
}

// src/ProductShellAttentionStrip/ProductShellAttentionStrip.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var SEVERITY_ICON = {
  error: /* @__PURE__ */ jsx3(BiErrorCircle, {}),
  warning: /* @__PURE__ */ jsx3(BiError, {}),
  info: /* @__PURE__ */ jsx3(BiInfoCircle, {})
};
var ProductShellAttentionStrip = ({ items, expanded = false, onToggle }) => {
  const top = topSeverity(items);
  if (top === null) return null;
  const count = items.length;
  return /* @__PURE__ */ jsxs2(
    Box3,
    {
      sx: {
        border: 1,
        borderColor: "divider",
        borderLeft: 4,
        borderLeftColor: `${top}.main`,
        borderRadius: 1,
        bgcolor: "background.paper",
        overflow: "hidden"
      },
      children: [
        /* @__PURE__ */ jsxs2(
          Box3,
          {
            role: "button",
            tabIndex: 0,
            onClick: onToggle,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") onToggle?.();
            },
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              cursor: onToggle ? "pointer" : "default",
              "&:hover": onToggle ? { bgcolor: "action.hover" } : void 0
            },
            children: [
              /* @__PURE__ */ jsx3(Box3, { sx: { color: `${top}.main`, fontSize: 20, display: "flex" }, children: SEVERITY_ICON[top] }),
              /* @__PURE__ */ jsxs2(Box3, { sx: { flex: 1, minWidth: 0, overflow: "hidden" }, children: [
                /* @__PURE__ */ jsxs2(Typography2, { component: "span", sx: { fontWeight: 600 }, children: [
                  count,
                  " ",
                  count === 1 ? "item needs" : "items need",
                  " attention"
                ] }),
                !expanded && /* @__PURE__ */ jsxs2(Typography2, { component: "span", sx: { color: "text.secondary", ml: 1 }, children: [
                  "\u2014 ",
                  items[0].title
                ] })
              ] }),
              /* @__PURE__ */ jsx3(Typography2, { variant: "body2", sx: { fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }, children: expanded ? "Hide" : "Show all" }),
              /* @__PURE__ */ jsx3(
                Box3,
                {
                  sx: {
                    color: "text.disabled",
                    fontSize: 20,
                    display: "flex",
                    transform: expanded ? "rotate(180deg)" : "none",
                    transition: "transform .15s ease"
                  },
                  children: /* @__PURE__ */ jsx3(BiChevronDown, {})
                }
              )
            ]
          }
        ),
        expanded && /* @__PURE__ */ jsx3(Box3, { sx: { borderTop: 1, borderColor: "divider" }, children: items.map((item, i) => {
          const meta = [item.detail, item.rooftopLabel].filter(Boolean).join(" \xB7 ");
          return /* @__PURE__ */ jsxs2(
            Box3,
            {
              role: item.onOpen ? "button" : void 0,
              tabIndex: item.onOpen ? 0 : void 0,
              onClick: item.onOpen,
              onKeyDown: item.onOpen ? (e) => {
                if (e.key === "Enter" || e.key === " ") item.onOpen?.();
              } : void 0,
              sx: {
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 1.5,
                cursor: item.onOpen ? "pointer" : "default",
                borderTop: i === 0 ? 0 : 1,
                borderColor: "divider",
                "&:hover": item.onOpen ? { bgcolor: "action.hover" } : void 0
              },
              children: [
                /* @__PURE__ */ jsx3(Box3, { sx: { color: `${item.severity}.main`, fontSize: 18, display: "flex", mt: "2px" }, children: SEVERITY_ICON[item.severity] }),
                /* @__PURE__ */ jsxs2(Box3, { sx: { flex: 1, minWidth: 0 }, children: [
                  /* @__PURE__ */ jsx3(Typography2, { variant: "body2", children: item.title }),
                  meta && /* @__PURE__ */ jsx3(Typography2, { variant: "caption", sx: { color: "text.secondary" }, children: meta })
                ] }),
                item.onOpen && /* @__PURE__ */ jsx3(Box3, { sx: { color: "text.disabled", fontSize: 18, display: "flex" }, children: /* @__PURE__ */ jsx3(BiChevronRight, {}) })
              ]
            },
            i
          );
        }) })
      ]
    }
  );
};
var ProductShellAttentionStrip_default = ProductShellAttentionStrip;

// src/ProductShellHeader/ProductShellHeader.tsx
import { useId } from "react";
import { Box as Box4, Skeleton, Typography as Typography3 } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var StatusPill = ({ label, tone }) => {
  const neutral = tone === "default";
  const fg = neutral ? "text.secondary" : `${tone}.main`;
  return /* @__PURE__ */ jsxs3(
    Box4,
    {
      component: "span",
      sx: (theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: "2px",
        borderRadius: "999px",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        color: fg,
        bgcolor: neutral ? alpha(theme.palette.text.primary, 0.08) : alpha(theme.palette[tone].main, 0.14)
      }),
      children: [
        /* @__PURE__ */ jsx4(Box4, { component: "span", sx: { width: 7, height: 7, borderRadius: "50%", bgcolor: fg, flex: "0 0 auto" } }),
        label
      ]
    }
  );
};
var ICON_BOX = 40;
var ICON_SIZE = 24;
var ICON_TINT_ALPHA = 0.12;
var NAME_FONT_SIZE = 24;
var DESCRIPTION_FONT_SIZE = 12;
var NAME_INSET_PX = 2;
var DESCRIPTION_INSET_PX = 2;
var TEXT_EDGE_EM = 0.1365;
var TEXT_OVERRUN_EM = 0.11;
var edgePx = (fontSize) => `-${(TEXT_EDGE_EM * fontSize).toFixed(2)}px`;
var ONE_LINE_SX = {
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1,
  py: `${TEXT_OVERRUN_EM}em`,
  my: `-${TEXT_OVERRUN_EM}em`
};
var ProductShellHeader = ({ identity, headerActions, pending = false }) => {
  const descriptionId = useId();
  if (pending) {
    return /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", alignItems: "center", gap: 1.5, minHeight: ICON_BOX }, children: [
      /* @__PURE__ */ jsx4(Skeleton, { variant: "rounded", width: ICON_BOX, height: ICON_BOX }),
      /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", flexDirection: "column", justifyContent: "space-between", height: ICON_BOX }, children: [
        /* @__PURE__ */ jsx4(Skeleton, { variant: "rounded", width: 200, height: NAME_FONT_SIZE }),
        /* @__PURE__ */ jsx4(Skeleton, { variant: "rounded", width: 240, height: DESCRIPTION_FONT_SIZE })
      ] })
    ] });
  }
  const { name, description, icon, accentColor, statusBadge } = identity;
  return /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }, children: [
    /* @__PURE__ */ jsx4(
      Box4,
      {
        "aria-hidden": true,
        sx: (theme) => {
          const accent = accentColor ?? theme.palette.text.primary;
          return {
            flex: "0 0 auto",
            width: ICON_BOX,
            height: ICON_BOX,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            fontSize: ICON_SIZE,
            color: accent,
            bgcolor: alpha(accent, ICON_TINT_ALPHA)
          };
        },
        children: icon
      }
    ),
    /* @__PURE__ */ jsxs3(
      Box4,
      {
        sx: {
          flex: "1 1 auto",
          minWidth: 0,
          height: ICON_BOX,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        },
        children: [
          /* @__PURE__ */ jsxs3(
            Box4,
            {
              sx: {
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                mt: edgePx(NAME_FONT_SIZE),
                pt: `${NAME_INSET_PX}px`
              },
              children: [
                /* @__PURE__ */ jsx4(
                  Typography3,
                  {
                    component: "h1",
                    "aria-describedby": description ? descriptionId : void 0,
                    sx: { ...ONE_LINE_SX, fontSize: NAME_FONT_SIZE, fontWeight: 600 },
                    children: name
                  }
                ),
                statusBadge && /* @__PURE__ */ jsx4(StatusPill, { label: statusBadge.label, tone: statusBadge.tone })
              ]
            }
          ),
          description && /* @__PURE__ */ jsx4(Box4, { sx: { minWidth: 0, mb: edgePx(DESCRIPTION_FONT_SIZE), pb: `${DESCRIPTION_INSET_PX}px` }, children: /* @__PURE__ */ jsx4(
            Typography3,
            {
              id: descriptionId,
              component: "p",
              sx: { ...ONE_LINE_SX, color: "text.secondary", fontSize: DESCRIPTION_FONT_SIZE },
              children: description
            }
          ) })
        ]
      }
    ),
    headerActions && /* @__PURE__ */ jsx4(Box4, { sx: { display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto", alignSelf: "center" }, children: headerActions })
  ] });
};
var ProductShellHeader_default = ProductShellHeader;

// src/ProductShellActions/ProductShellActions.tsx
import { useCallback, useState as useState2 } from "react";
import {
  Box as Box5,
  Button as Button3,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { BiChevronDown as BiChevronDown2, BiDotsVerticalRounded, BiRefresh } from "react-icons/bi";

// src/utils/headerActions.ts
var DEFAULT_REFRESH_LABEL = "Refresh";
var REFRESH_ACTION_KEY = "refresh";
function hasHeaderActions(model) {
  if (!model) return false;
  return (model.primary?.methods.length ?? 0) > 0 || model.secondary !== void 0 || model.sync !== void 0 || (model.overflow?.length ?? 0) > 0;
}
function foldHeaderActions(model, refreshLabel = DEFAULT_REFRESH_LABEL, refreshIcon) {
  const items = [];
  if (model.primary) items.push(...model.primary.methods);
  if (model.secondary) items.push(model.secondary);
  if (model.overflow) items.push(...model.overflow);
  if (model.sync) {
    const { onSync, label } = model.sync;
    items.push({ key: REFRESH_ACTION_KEY, label: label ?? refreshLabel, icon: refreshIcon, onSelect: () => void onSync() });
  }
  return items;
}

// src/ProductShellActions/ProductShellActions.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var OVERFLOW_LABEL = "More actions";
var ICON_SIZE2 = 24;
var linkProps = (action, LinkComponent) => {
  if (!action.to || action.disabledReason) return {};
  return LinkComponent ? { component: LinkComponent, to: action.to } : { component: "a", href: action.to };
};
var useMenuAnchor = () => {
  const [anchor, setAnchor] = useState2(null);
  const open = useCallback((event) => setAnchor(event.currentTarget), []);
  const close = useCallback(() => setAnchor(null), []);
  return { anchor, open, close };
};
var DisabledTooltip = ({ reason, children }) => reason ? /* @__PURE__ */ jsx5(Tooltip, { title: reason, placement: "bottom", arrow: true, children: /* @__PURE__ */ jsx5("span", { children }) }) : /* @__PURE__ */ jsx5(Fragment, { children });
var ActionButton = ({ action, variant, LinkComponent }) => /* @__PURE__ */ jsx5(DisabledTooltip, { reason: action.disabledReason, children: /* @__PURE__ */ jsx5(
  Button3,
  {
    variant,
    startIcon: action.icon,
    disabled: Boolean(action.disabledReason),
    onClick: action.onSelect,
    sx: { whiteSpace: "nowrap" },
    ...linkProps(action, LinkComponent),
    children: action.label
  }
) });
var ActionMenuItems = ({ items, onClose, LinkComponent }) => /* @__PURE__ */ jsx5(Fragment, { children: items.map((item) => /* @__PURE__ */ jsxs4(
  MenuItem,
  {
    disabled: Boolean(item.disabledReason),
    onClick: () => {
      onClose();
      item.onSelect?.();
    },
    ...linkProps(item, LinkComponent),
    children: [
      item.icon && /* @__PURE__ */ jsx5(ListItemIcon, { sx: { fontSize: ICON_SIZE2 }, children: item.icon }),
      /* @__PURE__ */ jsx5(ListItemText, { primary: item.label, secondary: item.disabledReason })
    ]
  },
  item.key
)) });
var PrimaryAction = ({ primary, LinkComponent }) => {
  const menu = useMenuAnchor();
  if (primary.methods.length === 1) {
    return /* @__PURE__ */ jsx5(ActionButton, { action: primary.methods[0], variant: "contained", LinkComponent });
  }
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx5(
      Button3,
      {
        variant: "contained",
        startIcon: primary.icon,
        endIcon: /* @__PURE__ */ jsx5(BiChevronDown2, {}),
        onClick: menu.open,
        "aria-haspopup": "menu",
        "aria-expanded": Boolean(menu.anchor),
        sx: { whiteSpace: "nowrap" },
        children: primary.label
      }
    ),
    /* @__PURE__ */ jsx5(Menu, { anchorEl: menu.anchor, open: Boolean(menu.anchor), onClose: menu.close, children: /* @__PURE__ */ jsx5(ActionMenuItems, { items: primary.methods, onClose: menu.close, LinkComponent }) })
  ] });
};
var SyncButton = ({ onSync, label }) => {
  const [pending, setPending] = useState2(false);
  const run = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onSync();
    } finally {
      setPending(false);
    }
  };
  return /* @__PURE__ */ jsx5(Tooltip, { title: label, placement: "bottom", children: /* @__PURE__ */ jsx5(IconButton, { "aria-label": label, onClick: () => void run(), disabled: pending, sx: { fontSize: ICON_SIZE2 }, children: pending ? /* @__PURE__ */ jsx5(CircularProgress, { size: ICON_SIZE2, color: "inherit" }) : /* @__PURE__ */ jsx5(BiRefresh, {}) }) });
};
var OverflowButton = ({ items, LinkComponent }) => {
  const menu = useMenuAnchor();
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx5(Tooltip, { title: OVERFLOW_LABEL, placement: "bottom", children: /* @__PURE__ */ jsx5(
      IconButton,
      {
        "aria-label": OVERFLOW_LABEL,
        "aria-haspopup": "menu",
        "aria-expanded": Boolean(menu.anchor),
        onClick: menu.open,
        sx: { fontSize: ICON_SIZE2 },
        children: /* @__PURE__ */ jsx5(BiDotsVerticalRounded, {})
      }
    ) }),
    /* @__PURE__ */ jsx5(Menu, { anchorEl: menu.anchor, open: Boolean(menu.anchor), onClose: menu.close, children: /* @__PURE__ */ jsx5(ActionMenuItems, { items, onClose: menu.close, LinkComponent }) })
  ] });
};
var ProductShellActions = ({ actions, LinkComponent, refreshLabel = DEFAULT_REFRESH_LABEL }) => {
  const theme = useTheme();
  const folded = useMediaQuery(theme.breakpoints.down("sm"));
  if (!hasHeaderActions(actions)) return null;
  if (folded) {
    return /* @__PURE__ */ jsx5(OverflowButton, { items: foldHeaderActions(actions, refreshLabel, /* @__PURE__ */ jsx5(BiRefresh, {})), LinkComponent });
  }
  const { primary, secondary, sync, overflow } = actions;
  return /* @__PURE__ */ jsxs4(Box5, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
    primary && primary.methods.length > 0 && /* @__PURE__ */ jsx5(PrimaryAction, { primary, LinkComponent }),
    secondary && /* @__PURE__ */ jsx5(ActionButton, { action: secondary, variant: "outlined", LinkComponent }),
    sync && /* @__PURE__ */ jsx5(SyncButton, { onSync: sync.onSync, label: sync.label ?? refreshLabel }),
    overflow && overflow.length > 0 && /* @__PURE__ */ jsx5(OverflowButton, { items: overflow, LinkComponent })
  ] });
};
var ProductShellActions_default = ProductShellActions;

// src/ProductShellTabs/ProductShellTabs.tsx
import { Box as Box6, Skeleton as Skeleton2, Tab, Tabs } from "@mui/material";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var STRIP_HEIGHT = 40;
var ProductShellTabs = ({ tabs, activeKey, onTabChange, pending = false, idBase }) => {
  if (pending) {
    return /* @__PURE__ */ jsx6(Box6, { sx: { display: "flex", alignItems: "center", gap: 3, px: 1, height: STRIP_HEIGHT }, children: [72, 88, 64].map((w) => /* @__PURE__ */ jsx6(Skeleton2, { variant: "text", width: w, height: 24 }, w)) });
  }
  if (tabs.length <= 1) return null;
  const active = tabs.find((tab) => tab.key === activeKey);
  const value = active ? active.key : false;
  return /* @__PURE__ */ jsx6(Box6, { children: /* @__PURE__ */ jsx6(
    Tabs,
    {
      value,
      onChange: (_, key) => onTabChange?.(key),
      variant: "scrollable",
      scrollButtons: "auto",
      allowScrollButtonsMobile: true,
      sx: { minHeight: STRIP_HEIGHT },
      children: tabs.map((tab) => /* @__PURE__ */ jsx6(
        Tab,
        {
          value: tab.key,
          disabled: tab.disabled,
          id: idBase ? `${idBase}-tab-${tab.key}` : void 0,
          "aria-controls": idBase ? `${idBase}-panel-${tab.key}` : void 0,
          sx: { minHeight: STRIP_HEIGHT, py: 0.5, textTransform: "none", fontWeight: 500 },
          label: /* @__PURE__ */ jsxs5(Box6, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.75 }, children: [
            tab.label,
            tab.badge != null && tab.badge > 0 && /* @__PURE__ */ jsx6(
              Box6,
              {
                component: "span",
                sx: {
                  px: 0.75,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  bgcolor: "action.selected",
                  color: "text.secondary",
                  fontSize: 11,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: tab.badge
              }
            )
          ] })
        },
        tab.key
      ))
    }
  ) });
};
var ProductShellTabs_default = ProductShellTabs;

// src/ProductShell/ProductShell.tsx
import { useId as useId2, useState as useState3 } from "react";
import { Box as Box7 } from "@mui/material";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var FILL_SX = { flex: "1 1 auto", minHeight: 0 };
var FILL_CONTENT_SX = { ...FILL_SX, display: "flex", flexDirection: "column" };
var ProductShell = ({
  identity,
  headerActions,
  banner,
  aboveTabs,
  tabs,
  activeKey,
  onTabChange,
  attentionItems,
  pending = false,
  fill = false,
  variant = "contained",
  maxWidth,
  children
}) => {
  const [attentionOpen, setAttentionOpen] = useState3(false);
  const baseId = useId2();
  const hasStrip = !pending && !!tabs && tabs.length > 1;
  const showsStrip = !!tabs && (pending || tabs.length > 1);
  const activeTab = tabs?.find((tab) => tab.key === activeKey);
  const linkTabpanel = hasStrip && activeTab != null;
  const fullscreen = variant === "fullscreen";
  return /* @__PURE__ */ jsxs6(Box7, { sx: { display: "flex", flexDirection: "column", ...fill ? FILL_SX : {} }, children: [
    /* @__PURE__ */ jsx7(
      Box7,
      {
        sx: fullscreen ? { flex: "0 0 auto", bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" } : { flex: "0 0 auto", width: "100%", maxWidth, mx: "auto", px: 3, pt: 3 },
        children: /* @__PURE__ */ jsxs6(
          Box7,
          {
            sx: {
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pb: showsStrip ? 0 : 1.5,
              ...fullscreen ? { px: 3, pt: 3 } : { borderBottom: 1, borderColor: "divider" }
            },
            children: [
              /* @__PURE__ */ jsx7(ProductShellHeader_default, { identity, headerActions, pending }),
              /* @__PURE__ */ jsx7(
                ProductShellAttentionStrip_default,
                {
                  items: attentionItems ?? [],
                  expanded: attentionOpen,
                  onToggle: () => setAttentionOpen((open) => !open)
                }
              ),
              banner,
              aboveTabs,
              tabs && /* @__PURE__ */ jsx7(
                ProductShellTabs_default,
                {
                  tabs,
                  activeKey,
                  onTabChange,
                  pending,
                  idBase: baseId
                }
              )
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx7(
      Box7,
      {
        role: hasStrip ? "tabpanel" : void 0,
        id: linkTabpanel ? `${baseId}-panel-${activeTab.key}` : void 0,
        "aria-labelledby": linkTabpanel ? `${baseId}-tab-${activeTab.key}` : void 0,
        tabIndex: hasStrip ? 0 : void 0,
        sx: { minWidth: 0, ...fill ? FILL_CONTENT_SX : {} },
        children
      }
    )
  ] });
};
var ProductShell_default = ProductShell;

// src/ProductShellExperience/ProductShellExperience.tsx
import { Box as Box8 } from "@mui/material";
import { PAGE_SX } from "@hopdrive/sdk-theme";

// src/resolveShellTreatment.ts
var UNAVAILABLE = { banner: null, marketing: false, canCreate: false };
function resolveShellTreatment(access) {
  if (!access) return { ...UNAVAILABLE };
  switch (access.state) {
    case "available":
      return { banner: null, marketing: false, canCreate: access.canCreate };
    case "limited":
      return { banner: "limited", marketing: false, canCreate: access.canCreate };
    case "read_only":
      return { banner: "read_only", marketing: false, canCreate: false };
    case "marketing":
      return { banner: null, marketing: true, canCreate: false };
    case "hidden":
    default:
      return { ...UNAVAILABLE };
  }
}

// src/resolveStatusBadge.ts
var FULL_ACCESS = { label: "Full Access", tone: "success" };
var PARTIAL_ACCESS = { label: "Partial Access", tone: "warning" };
var CONTACT_SALES = { label: "Contact Sales", tone: "info" };
function resolveStatusBadge(access) {
  if (!access) return null;
  switch (access.state) {
    case "available":
      return access.canCreate ? FULL_ACCESS : PARTIAL_ACCESS;
    case "limited":
    case "read_only":
      return PARTIAL_ACCESS;
    case "marketing":
      return CONTACT_SALES;
    case "hidden":
    default:
      return null;
  }
}

// src/ProductShellExperience/ProductShellExperience.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function withAccessStatusBadge(identity, access) {
  const statusBadge = resolveStatusBadge(
    access ? { ...access, canCreate: access.capabilities.createNew === true } : void 0
  );
  return { ...identity, statusBadge: statusBadge ?? void 0 };
}
var ProductShellExperience = ({
  pending = false,
  access,
  identity,
  tabs,
  activeKey,
  onTabChange,
  actions,
  LinkComponent,
  banner,
  suppressStateBanner = false,
  aboveTabs,
  onStateAction,
  marketing,
  advertiseContent,
  fill,
  variant,
  children
}) => {
  const treatment = resolveShellTreatment(access);
  const shellIdentity = withAccessStatusBadge(identity, access);
  if (pending) return /* @__PURE__ */ jsx8(ProductShell_default, { pending: true, identity: shellIdentity, tabs, variant });
  if (treatment.marketing) {
    return /* @__PURE__ */ jsx8(ProductShell_default, { identity: shellIdentity, attentionItems: [], variant, children: marketing ?? /* @__PURE__ */ jsx8(Box8, { sx: PAGE_SX, children: /* @__PURE__ */ jsx8(ProductAdvertise_default, { content: advertiseContent }) }) });
  }
  const headerActions = actions && hasHeaderActions(actions) ? /* @__PURE__ */ jsx8(ProductShellActions_default, { actions, LinkComponent }) : void 0;
  const stateBanner = /* @__PURE__ */ jsxs7(Fragment2, { children: [
    treatment.banner && !suppressStateBanner && /* @__PURE__ */ jsx8(
      ProductShellStateBanner_default,
      {
        variant: treatment.banner,
        reason: access?.reason,
        actions: access?.actions,
        onStateAction
      }
    ),
    banner
  ] });
  return /* @__PURE__ */ jsx8(
    ProductShell_default,
    {
      identity: shellIdentity,
      headerActions,
      banner: stateBanner,
      aboveTabs,
      tabs,
      activeKey,
      onTabChange,
      attentionItems: [],
      fill,
      variant,
      children
    }
  );
};
var ProductShellExperience_default = ProductShellExperience;
export {
  ProductAdvertise_default as ProductAdvertise,
  ProductShell_default as ProductShell,
  ProductShellActions_default as ProductShellActions,
  ProductShellAttentionStrip_default as ProductShellAttentionStrip,
  ProductShellExperience_default as ProductShellExperience,
  ProductShellHeader_default as ProductShellHeader,
  ProductShellStateBanner_default as ProductShellStateBanner,
  ProductShellTabs_default as ProductShellTabs,
  foldHeaderActions,
  hasHeaderActions,
  resolveShellTreatment,
  resolveStatusBadge,
  withAccessStatusBadge
};
//# sourceMappingURL=index.mjs.map